import * as React from 'react';
import { Copy, Edit3, Info, Link2Off, Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableStatusBadge,
  DashboardTableStatusTone,
  buildDashboardDataTableColumns,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
} from '@shigo-ui/dashboard-ui';
import { Button, Tooltip, TooltipContent, TooltipTrigger, toast } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { downloadCsv } from '../../../../../lib/downloadBlob';
import { countActiveAppRequestSecondaryFilters } from '../../appRequestsRouteSearch';
import { AppRequestsFilters } from '../../AppRequestsFilters';
import { useAppRequestsQuery, useSupplyAppsMutations } from '../../hooks';
import {
  AppRequestBooleanFilter,
  SupplyAppsSortDirection,
  supplyAppsPageSizes,
  type AppRequest,
} from '../../model';
import { useAppRequestsRouteFilters } from '../../useAppRequestsRouteFilters';
import { AppRequestEditorDialog } from '../AppRequestEditorDialog/AppRequestEditorDialog';
import { CreateAppFromRequestDialog } from '../CreateAppFromRequestDialog/CreateAppFromRequestDialog';
import { AppRequestsTableToolbar } from './AppRequestsTableToolbar';
import { formatDisplayDate, numberFormat } from '../../../../../lib/format';
import { copyToClipboard } from '../../../../../lib/clipboard';

type OpenDialog =
  { type: 'edit'; requests: AppRequest[] } | { type: 'create'; request: AppRequest } | null;

export function AppRequestsTable({ publisherId }: { publisherId?: string } = {}) {
  const { can } = useShigoPermissions();
  const route = useAppRequestsRouteFilters(Boolean(publisherId));
  const { filter } = route;
  const scopedFilter = publisherId
    ? {
        ...filter,
        publisherIds: [publisherId],
        processed: AppRequestBooleanFilter.Yes,
        connectedWithApp: AppRequestBooleanFilter.Yes,
      }
    : filter;
  const query = useAppRequestsQuery(scopedFilter);
  const mutations = useSupplyAppsMutations();
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());
  const [dialog, setDialog] = React.useState<OpenDialog>(null);
  const [pendingIds, setPendingIds] = React.useState<Set<string>>(() => new Set());
  const activeFilterCount = countActiveAppRequestSecondaryFilters(filter);
  const [filtersOpen, setFiltersOpen] = React.useState(activeFilterCount > 0);
  const rows = query.data?.content ?? [];

  const canEdit = can(ShigoPermission.SupplyAppRequestEdit);
  const canDisconnect = !publisherId && can(ShigoPermission.SupplyAppRequestDisconnect);
  const canCreate = can(ShigoPermission.SupplyAppRequestCreate);
  function toggle(request: AppRequest, checked: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(request.requestKey);
      else next.delete(request.requestKey);
      return next;
    });
  }
  async function disconnect(request: AppRequest) {
    setPendingIds((current) => new Set(current).add(request.requestKey));
    try {
      await mutations.processRequests.mutateAsync([{ ...request, appId: null, processed: false }]);
      toast.success(dashboardT('supplyApps.request.disconnected'));
    } catch (error) {
      showRequestError(error, dashboardT('supplyApps.request.saveError'));
    } finally {
      setPendingIds((current) => {
        const next = new Set(current);
        next.delete(request.requestKey);
        return next;
      });
    }
  }

  const columns = buildDashboardDataTableColumns<AppRequest>([
    {
      id: 'selection',
      kind: DashboardDataTableColumnKind.Selection,
      checked: (request) => selected.has(request.requestKey),
      label: (request) =>
        dashboardT('supplyApps.request.select', {
          name: request.requestAppName ?? request.bundleId ?? request.requestKey,
        }),
      onChange: toggle,
      hidden: Boolean(publisherId) || rows.length === 0,
    },
    {
      id: 'adRequests',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyApps.request.adRequests'),
      headerClassName: 'w-32',
      className: 'w-32',
      sortable: true,
      sortKey: 'adRequests',
      value: (request) => numberFormat().format(request.adRequests),
      hidden: Boolean(publisherId),
    },
    {
      id: 'bundleId',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyApps.bundleId'),
      headerClassName: 'w-52',
      className: 'w-52 truncate',
      sortable: true,
      sortKey: 'bundleId',
      value: (request) => request.bundleId,
    },
    {
      id: 'processed',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('supplyApps.request.processed'),
      headerClassName: 'w-28',
      className: 'w-28',
      cell: (request) => (
        <DashboardTableStatusBadge
          tone={
            request.processed ? DashboardTableStatusTone.Success : DashboardTableStatusTone.Neutral
          }
        >
          {dashboardT(request.processed ? 'supplyApps.request.yes' : 'supplyApps.request.no')}
        </DashboardTableStatusBadge>
      ),
      hidden: Boolean(publisherId),
    },
    {
      id: 'blocked',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('supplyApps.request.blocked'),
      headerClassName: 'w-28',
      className: 'w-28',
      cell: (request) => (
        <DashboardTableStatusBadge
          tone={
            request.blocked ? DashboardTableStatusTone.Warning : DashboardTableStatusTone.Neutral
          }
        >
          {dashboardT(request.blocked ? 'supplyApps.request.yes' : 'supplyApps.request.no')}
        </DashboardTableStatusBadge>
      ),
    },
    {
      id: 'suggested',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyApps.request.suggestedApp'),
      headerClassName: 'w-56',
      className: 'w-56 truncate',
      sortable: true,
      sortKey: 'suggestedAppName',
      value: (request) => request.suggestedAppName,
    },
    {
      id: 'connected',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyApps.request.connectedApp'),
      headerClassName: 'w-56',
      className: 'w-56 truncate',
      sortable: true,
      sortKey: 'connectedAppName',
      value: (request) => request.connectedAppName,
    },
    {
      id: 'requestName',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyApps.request.requestName'),
      headerClassName: 'w-52',
      className: 'w-52 truncate',
      value: (request) => request.requestAppName,
      hidden: Boolean(publisherId),
    },
    {
      id: 'publisher',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyApps.publisher'),
      headerClassName: 'w-48',
      className: 'w-48 truncate',
      sortable: true,
      sortKey: 'publisherName',
      value: (request) => request.publisherName,
      hidden: Boolean(publisherId),
    },
    {
      id: 'platform',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyApps.platform'),
      headerClassName: 'w-32',
      className: 'w-32',
      sortable: true,
      sortKey: 'platformName',
      value: (request) => request.platformName,
    },
    {
      id: 'url',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyApps.appStoreUrl'),
      headerClassName: 'w-64',
      className: 'w-64 truncate',
      value: ({ appStoreUrl: url }) =>
        url ? (
          <div className="flex min-w-0 items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="min-w-0 truncate text-left">
                  {url}
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-80 break-all">{url}</TooltipContent>
            </Tooltip>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              aria-label={dashboardT('supplyApps.request.copyUrl')}
              onClick={() => {
                void copyToClipboard(url, {
                  successMessage: dashboardT('supplyApps.request.urlCopied'),
                  errorMessage: dashboardT('supplyApps.request.urlCopyError'),
                });
              }}
            >
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        ) : null,
    },
    {
      id: 'created',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('supplyApps.request.created'),
      headerClassName: 'w-32',
      className: 'w-32 whitespace-nowrap',
      sortable: true,
      sortKey: 'createdOn',
      cell: (request) =>
        request.processedAt || request.processedBy ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground h-7 w-7 shrink-0"
                aria-label={dashboardT('supplyApps.request.processingDetails')}
              >
                <Info className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-80 text-left break-words whitespace-normal">
              <div>
                {formatDisplayDate(request.processedAt, {
                  month: '2-digit',
                  day: '2-digit',
                  year: 'numeric',
                })}
              </div>
              <div>{request.processedBy || '-'}</div>
            </TooltipContent>
          </Tooltip>
        ) : null,
      hidden: Boolean(publisherId),
    },
    {
      id: 'processedBy',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('supplyApps.request.processedBy'),
      headerClassName: 'w-48',
      className: 'w-48 truncate',
      value: (request) => request.processedBy,
      hidden: !publisherId,
    },
    {
      id: 'processedAt',
      kind: DashboardDataTableColumnKind.Date,
      header: dashboardT('supplyApps.request.processedAt'),
      headerClassName: 'w-32',
      className: 'w-32 whitespace-nowrap',
      value: (request) => formatDisplayDate(request.processedAt),
      hidden: !publisherId,
    },
    {
      id: 'note',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('supplyApps.request.note'),
      headerClassName: 'w-20 text-center',
      className: 'w-20 text-center',
      cell: (request) =>
        request.note ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={dashboardT('supplyApps.request.note')}
              >
                <Info className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-80 whitespace-normal">{request.note}</TooltipContent>
          </Tooltip>
        ) : null,
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      cell: (request) =>
        canEdit || canDisconnect || canCreate ? (
          <DashboardTableRowActions label={dashboardT('supplyApps.request.actions')}>
            {canEdit ? (
              <DashboardTableRowActionItem
                disabled={Boolean(publisherId && request.networkId)}
                onSelect={() => {
                  setDialog({ type: 'edit', requests: [request] });
                }}
              >
                <Edit3 className="h-4 w-4" />
                {dashboardT('supplyApps.request.edit')}
              </DashboardTableRowActionItem>
            ) : null}
            {canDisconnect && request.appId ? (
              <DashboardTableRowActionItem
                disabled={pendingIds.has(request.requestKey)}
                onSelect={() => void disconnect(request)}
              >
                <Link2Off className="h-4 w-4" />
                {dashboardT('supplyApps.request.disconnect')}
              </DashboardTableRowActionItem>
            ) : null}
            {canCreate ? (
              <DashboardTableRowActionItem
                disabled={Boolean(publisherId && request.networkId)}
                onSelect={() => {
                  setDialog({ type: 'create', request });
                }}
              >
                <Plus className="h-4 w-4" />
                {dashboardT('supplyApps.request.createApp')}
              </DashboardTableRowActionItem>
            ) : null}
          </DashboardTableRowActions>
        ) : null,
    },
  ]);

  async function exportCsv() {
    try {
      const csv = await mutations.exportRequests.mutateAsync(scopedFilter);
      downloadCsv(csv, 'app-requests.csv');
      toast.success(dashboardT('supplyApps.request.exportSuccess'));
    } catch (error) {
      showRequestError(error, dashboardT('supplyApps.request.exportError'));
    }
  }
  function closeDialog() {
    setDialog(null);
  }
  const selectedRequests = rows.filter((request) => selected.has(request.requestKey));
  return (
    <>
      <DashboardDataTable
        title={dashboardT('supplyApps.requestsTitle')}
        tableLabel={dashboardT('supplyApps.requestsTitle')}
        columns={columns}
        items={rows}
        getRowKey={(request) => request.requestKey}
        loading={query.isLoading}
        loadingMessage={dashboardT('supplyApps.request.loading')}
        errorMessage={query.isError ? dashboardT('supplyApps.request.loadError') : undefined}
        emptyMessage={dashboardT('supplyApps.request.empty')}
        minWidthClassName="min-w-[2200px]"
        toolbar={
          <AppRequestsTableToolbar
            filter={scopedFilter}
            activeFilterCount={activeFilterCount}
            filtersOpen={filtersOpen}
            selectedCount={selectedRequests.length}
            canEdit={canEdit}
            canExport={can(ShigoPermission.SupplyAppRequestsExport)}
            exportPending={mutations.exportRequests.isPending}
            onSearchChange={route.setSearch}
            onDatePresetChange={route.setDatePreset}
            onFiltersOpenChange={setFiltersOpen}
            onClearFilters={route.resetSecondaryFilters}
            onEditSelected={() => {
              setDialog({ type: 'edit', requests: selectedRequests });
            }}
            onRefresh={() => void query.refetch()}
            onExport={() => void exportCsv()}
            publisherScoped={Boolean(publisherId)}
          />
        }
        filterToolbar={
          filtersOpen ? (
            <div id="app-requests-filter-toolbar">
              <AppRequestsFilters
                filter={scopedFilter}
                onPublishersChange={publisherId ? undefined : route.setPublishers}
                onPlatformsChange={route.setPlatforms}
                onProcessedChange={publisherId ? undefined : route.setProcessed}
                onBlockedChange={route.setBlocked}
                onConnectedChange={publisherId ? undefined : route.setConnected}
                onBundleIdsChange={route.setBundleIds}
                onAppStoreUrlsChange={route.setAppStoreUrls}
                publisherScoped={Boolean(publisherId)}
              />
            </div>
          ) : undefined
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: supplyAppsPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            route.setPagination(next.page, next.pageSize);
          },
        }}
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === SupplyAppsSortDirection.Ascending
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (next) => {
            if (!next) {
              route.setSort(null, filter.sortDirection);
              return;
            }
            route.setSort(
              next.key as typeof filter.sortProperty,
              next.direction === DashboardDataTableSortDirection.Asc
                ? SupplyAppsSortDirection.Ascending
                : SupplyAppsSortDirection.Descending,
            );
          },
        }}
      />
      {dialog?.type === 'edit' ? (
        <AppRequestEditorDialog
          requests={dialog.requests}
          onSaved={() => {
            setSelected(new Set());
          }}
          onClose={closeDialog}
        />
      ) : null}
      {dialog?.type === 'create' ? (
        <CreateAppFromRequestDialog request={dialog.request} onClose={closeDialog} />
      ) : null}
    </>
  );
}
